import { dashboardIssueWorkspaceCreateRequestSchema } from "@arti/schemas/dashboard";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { z } from "zod";

import type {
  IssueDetail,
  IssueWorkspaceLaunchRequest,
  IssueWorkspaceLaunchResponse,
  WizardEnvironmentSelection,
  WizardRepositorySelection,
} from "./types";

import { useToast } from "../../../context/ToastContext";
import { useOptionalDashboardAuth } from "../../../hooks/useDashboardAuth";
import { isAdmin } from "../../../utils/rbac";
import { Button } from "../../common/Button";
import { Modal } from "../../common/Modal";
import { WizardStepper } from "../../common/WizardStepper";
import { RetroError } from "../retro";
import {
  describeWizardErrorCode,
  launchIssueWorkspace,
  WizardApiError,
} from "./api";
import { sanitizeChannelName } from "./channelName";
import { ProgressView } from "./ProgressView";
import { SetupStep, type TemplateSelectionTarget } from "./SetupStep";
import { TicketStep } from "./TicketStep";
import { nextWakeBlockToast } from "./wakeBlockToast";

const STEPS = [
  { id: "ticket", label: "Ticket" },
  { id: "setup", label: "Setup" },
  { id: "launch", label: "Launch" },
] as const;

type StepId = (typeof STEPS)[number]["id"];

const DRAFT_KEY = "issue-wizard-draft";
const SNAPSHOT_POLL_MS = 4000;

type PersistedDraft = z.infer<typeof persistedDraftSchema>;

const persistedDraftSchema = z.object({
  request: dashboardIssueWorkspaceCreateRequestSchema,
});

type LaunchDefinition = Omit<IssueWorkspaceLaunchRequest, "idempotencyKey">;

/** Key order is fixed by the construction sites, so stringify is canonical. */
function definitionFingerprint(definition: LaunchDefinition): string {
  return JSON.stringify(definition);
}

// No error response ever removes the draft: any failure class (auth, org
// scope, capacity, transport) can transiently reject a REPLAY of an already
// accepted operation, and replays are idempotent (definition-stable key), so
// a dead draft costs one error banner per dialog open within the tab session
// — never a duplicate workspace. Only a settled snapshot clears it.

/** Draft persistence is IDs + idempotency key only (no ticket body): reload
 * re-POSTs the identical request, which attaches to the live operation or
 * resumes a settled partial one. */
function loadDraft(): PersistedDraft | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? persistedDraftSchema.parse(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function describeLaunchError(error: unknown): string {
  if (error instanceof WizardApiError && error.code) {
    return describeWizardErrorCode(error) ?? `${error.code}: ${error.message}`;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

/**
 * Actor display names must be unique within a launch — the server rejects
 * duplicates after lowercasing, so comparison is case-insensitive. Two
 * distinct templates can share a name; suffix with " 2", " 3", … until free,
 * truncating the base so candidates stay within the 256-char schema limit.
 */
const ACTOR_DISPLAY_NAME_MAX_LENGTH = 256;

export function uniqueActorDisplayName(
  base: string,
  taken: readonly string[],
): string {
  const takenLower = new Set(taken.map((name) => name.toLowerCase()));
  const cappedBase = base.slice(0, ACTOR_DISPLAY_NAME_MAX_LENGTH);
  if (!takenLower.has(cappedBase.toLowerCase())) {
    return cappedBase;
  }
  for (let n = 2; ; n += 1) {
    const suffix = ` ${n}`;
    const candidate = `${cappedBase.slice(0, ACTOR_DISPLAY_NAME_MAX_LENGTH - suffix.length)}${suffix}`;
    if (!takenLower.has(candidate.toLowerCase())) {
      return candidate;
    }
  }
}

interface IssueWizardDialogProps {
  onClose: () => void;
  onOpenChannel: (channelId: string) => void;
}

export const IssueWizardDialog: FC<IssueWizardDialogProps> = ({
  onClose,
  onOpenChannel,
}) => {
  const [step, setStep] = useState<StepId>("ticket");
  const [issue, setIssue] = useState<IssueDetail | null>(null);
  const [templateSelections, setTemplateSelections] = useState<
    { templateId: string; displayName: string }[]
  >([]);
  const [leadTemplateId, setLeadTemplateId] = useState<string | null>(null);
  const [environment, setEnvironment] =
    useState<WizardEnvironmentSelection | null>(null);
  const [environmentTouched, setEnvironmentTouched] = useState(false);
  const [setupValid, setSetupValid] = useState(true);
  const [channelNameOverride, setChannelNameOverride] = useState("");
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<IssueWorkspaceLaunchResponse | null>(
    null,
  );
  const [savedRepos, setSavedRepos] = useState<WizardRepositorySelection[]>([]);
  const prefersReducedMotion = useReducedMotion();
  const requestRef = useRef<IssueWorkspaceLaunchRequest | null>(null);
  // Same launch definition → same idempotency key, so retries after ambiguous
  // failures attach to the accepted operation instead of duplicating it.
  const keyRef = useRef<{ fingerprint: string; key: string } | null>(null);
  const [pollTick, setPollTick] = useState(0);
  const toast = useToast();
  // Optional so the dialog still renders in bare test trees; absent auth means
  // the least-privileged view (the server enforces the real boundary).
  const auth = useOptionalDashboardAuth();
  const canManageOrgSettings = isAdmin(
    auth?.orgRole ?? null,
    auth?.globalRole ?? null,
  );
  const wakeBlockToastKeyRef = useRef<string | null>(null);

  // Server-computed on issue detail; collision suffixes are applied at launch.
  const generatedChannelName = issue?.suggestedChannelName ?? "";
  const effectiveChannelName =
    sanitizeChannelName(channelNameOverride) || generatedChannelName;

  // A launch interrupted by reload resumes: re-POST the persisted request and
  // jump straight to progress. The coordinator attaches; nothing re-executes.
  useEffect(() => {
    const draft = loadDraft();
    if (!draft) {
      return;
    }
    requestRef.current = draft.request;
    const { idempotencyKey, ...definition } = draft.request;
    keyRef.current = {
      fingerprint: definitionFingerprint(definition),
      key: idempotencyKey,
    };
    setStep("launch");
    setLaunching(true);
    void launchIssueWorkspace(draft.request)
      .then((response) => setSnapshot(response))
      .catch((error: unknown) => {
        setLaunchError(error instanceof Error ? error.message : String(error));
        setStep("ticket");
      })
      .finally(() => setLaunching(false));
  }, []);

  // Snapshot polling while the operation is in progress. pollTick re-arms the
  // timer after a failed poll, when snapshot identity alone wouldn't; accepted
  // work is never cancelled by the client.
  useEffect(() => {
    if (!snapshot || snapshot.state !== "in_progress" || !requestRef.current) {
      return;
    }
    const request = requestRef.current;
    let cancelled = false;
    const timer = setTimeout(() => {
      void launchIssueWorkspace(request)
        .then((response) => {
          if (!cancelled) {
            setSnapshot(response);
          }
        })
        .catch(() => {
          // Transient poll failure: keep the last snapshot.
        })
        .finally(() => {
          if (!cancelled) {
            setPollTick((tick) => tick + 1);
          }
        });
    }, SNAPSHOT_POLL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [snapshot, pollTick]);

  useEffect(() => {
    if (snapshot && snapshot.state !== "in_progress") {
      sessionStorage.removeItem(DRAFT_KEY);
    }
  }, [snapshot]);

  // One warning toast per wake-block transition. Polling re-POSTs produce a new
  // snapshot object every few seconds with identical content, so the dedupe key
  // (operation + recipient + reason) — not snapshot identity — decides.
  useEffect(() => {
    if (!snapshot) {
      return;
    }
    const { key, toast: payload } = nextWakeBlockToast(
      wakeBlockToastKeyRef.current,
      snapshot,
    );
    wakeBlockToastKeyRef.current = key;
    if (payload) {
      toast.present(payload);
    }
  }, [snapshot, toast]);

  const toggleTemplate = useCallback((template: TemplateSelectionTarget) => {
    setTemplateSelections((prev) => {
      const exists = prev.some((entry) => entry.templateId === template.id);
      const next = exists
        ? prev.filter((entry) => entry.templateId !== template.id)
        : [
            ...prev,
            {
              displayName: uniqueActorDisplayName(
                template.name,
                prev.map((entry) => entry.displayName),
              ),
              templateId: template.id,
            },
          ];
      setLeadTemplateId((lead) => {
        if (exists && lead === template.id) {
          return next[0]?.templateId ?? null;
        }
        return lead ?? next[0]?.templateId ?? null;
      });
      return next;
    });
  }, []);

  const launch = useCallback(() => {
    if (!issue || templateSelections.length === 0 || !leadTemplateId) {
      return;
    }
    const definition: LaunchDefinition = {
      actors: templateSelections,
      channelDisplayName: effectiveChannelName,
      environment: environment
        ? {
            cpuCount: environment.cpuCount,
            profileId: environment.profileId,
            ramMB: environment.ramMB,
            repositories: environment.repositories,
            storageMB: environment.storageMB,
          }
        : null,
      issueKey: issue.issueKey,
      leadTemplateId,
      providerId: issue.providerId,
    };
    const fingerprint = definitionFingerprint(definition);
    if (keyRef.current?.fingerprint !== fingerprint) {
      keyRef.current = { fingerprint, key: crypto.randomUUID() };
    }
    const request: IssueWorkspaceLaunchRequest = {
      ...definition,
      idempotencyKey: keyRef.current.key,
    };
    requestRef.current = request;
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ request }));
    setLaunching(true);
    setLaunchError(null);
    setStep("launch");
    void launchIssueWorkspace(request)
      .then((response) => setSnapshot(response))
      .catch((error: unknown) => {
        setLaunchError(describeLaunchError(error));
      })
      .finally(() => setLaunching(false));
  }, [
    issue,
    templateSelections,
    leadTemplateId,
    environment,
    effectiveChannelName,
  ]);

  const retry = useCallback(() => {
    const request = requestRef.current;
    if (!request) {
      return;
    }
    setLaunching(true);
    void launchIssueWorkspace(request)
      .then((response) => setSnapshot(response))
      .catch((error: unknown) => {
        setLaunchError(error instanceof Error ? error.message : String(error));
      })
      .finally(() => setLaunching(false));
  }, []);

  const completedSteps = useMemo(() => {
    const done = new Set<string>();
    if (issue) {
      done.add("ticket");
    }
    if (templateSelections.length > 0 && leadTemplateId) {
      done.add("setup");
    }
    if (snapshot && snapshot.state !== "in_progress") {
      done.add("launch");
    }
    return done;
  }, [issue, templateSelections, leadTemplateId, snapshot]);

  const canNext =
    (step === "ticket" && issue !== null) ||
    (step === "setup" &&
      templateSelections.length > 0 &&
      leadTemplateId &&
      setupValid);

  let disabledHint: string | null = null;
  if (!canNext && step === "ticket") {
    disabledHint = "Select a ticket to continue";
  } else if (!canNext && step === "setup") {
    disabledHint = setupValid
      ? "Choose at least one agent to continue"
      : "Resolve the unavailable selection to continue";
  }

  const footer =
    step === "launch" && (snapshot || launching) ? null : (
      <div className="flex w-full items-center justify-between">
        <Button
          size="sm"
          variant="outline"
          onClick={
            step === "ticket"
              ? onClose
              : () => setStep(step === "launch" ? "setup" : "ticket")
          }
        >
          {step === "ticket" ? "Cancel" : "Back"}
        </Button>
        <div className="flex min-w-0 items-center gap-3">
          {disabledHint && (
            <span className="truncate font-mono text-[10px] text-slate-500 dark:text-slate-400">
              {disabledHint}
            </span>
          )}
          {step !== "launch" ? (
            <Button
              size="sm"
              disabled={!canNext}
              onClick={() => setStep(step === "ticket" ? "setup" : "launch")}
            >
              {step === "setup" ? "Review launch" : "Next"}
            </Button>
          ) : (
            <Button
              size="sm"
              disabled={launching}
              loading={launching}
              loadingText="Creating…"
              onClick={launch}
            >
              Create workspace
            </Button>
          )}
        </div>
      </div>
    );

  return (
    <Modal
      title="New workspace from Jira"
      subtitle="Pick a ticket, choose agents, launch a ready channel."
      onClose={onClose}
      widthClass="max-w-2xl"
      footer={footer}
    >
      <div className="space-y-5">
        <WizardStepper
          steps={[...STEPS]}
          currentStepId={step}
          completedStepIds={completedSteps}
          ariaLabel="Workspace creation progress"
        />

        {launchError && <RetroError message={launchError} />}

        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={step}
            initial={
              prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }
            }
            animate={
              prefersReducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }
            }
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.15 }}
          >
            {step === "ticket" && (
              <TicketStep selectedIssue={issue} onSelectIssue={setIssue} />
            )}

            {step === "setup" && (
              <SetupStep
                issue={issue}
                selections={templateSelections}
                leadTemplateId={leadTemplateId}
                environment={environment}
                environmentTouched={environmentTouched}
                savedRepos={savedRepos}
                onSaveRepos={setSavedRepos}
                channelName={channelNameOverride}
                generatedChannelName={generatedChannelName}
                onToggleTemplate={toggleTemplate}
                onSetLead={setLeadTemplateId}
                onSetEnvironment={setEnvironment}
                onSetEnvironmentTouched={() => setEnvironmentTouched(true)}
                onSetChannelName={setChannelNameOverride}
                onValidityChange={setSetupValid}
              />
            )}

            {step === "launch" && !snapshot && !launching && issue && (
              <LaunchReview
                issue={issue}
                effectiveChannelName={effectiveChannelName}
                templateSelections={templateSelections}
                leadTemplateId={leadTemplateId}
                environment={environment}
              />
            )}

            {step === "launch" && (snapshot || launching) && (
              <>
                {snapshot ? (
                  <ProgressView
                    snapshot={snapshot}
                    polling={snapshot.state === "in_progress"}
                    canManageOrgSettings={canManageOrgSettings}
                    retrying={launching}
                    onRetry={retry}
                    onOpenChannel={() => {
                      onOpenChannel(snapshot.channel.id);
                      onClose();
                    }}
                  />
                ) : (
                  <div className="py-8 text-center font-mono text-[11px] text-slate-500 dark:text-slate-400">
                    Creating workspace…
                  </div>
                )}
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </Modal>
  );
};

interface LaunchReviewProps {
  issue: IssueDetail;
  effectiveChannelName: string;
  templateSelections: IssueWorkspaceLaunchRequest["actors"];
  leadTemplateId: string | null;
  environment: WizardEnvironmentSelection | null;
}

function LaunchReview({
  issue,
  effectiveChannelName,
  templateSelections,
  leadTemplateId,
  environment,
}: LaunchReviewProps) {
  return (
    <div className="space-y-3">
      <div className="border border-slate-200 dark:border-slate-800 px-3 py-2 font-mono text-[11px] text-slate-600 dark:text-slate-300">
        <div>
          <span className="text-slate-500 dark:text-slate-400">Ticket </span>
          {issue.issueKey} · {issue.summary}
        </div>
        <div>
          <span className="text-slate-500 dark:text-slate-400">Channel </span>#
          {effectiveChannelName}
        </div>
        <div>
          <span className="text-slate-500 dark:text-slate-400">Agents </span>
          {templateSelections
            .map(
              (entry) =>
                `${entry.displayName}${entry.templateId === leadTemplateId ? " (lead)" : ""}`,
            )
            .join(", ")}
        </div>
        <div>
          <span className="text-slate-500 dark:text-slate-400">Sandbox </span>
          {environment
            ? `${environment.profileId} · ${environment.repositories.length} repo${environment.repositories.length === 1 ? "" : "s"}`
            : "none"}
        </div>
      </div>
    </div>
  );
}
